import { FC, useEffect, useMemo, useState } from 'react';


import { AdminBugoBrandTable } from './AdminBugoBrandTable';
import { AnalyticsCalendar } from './AnalyticsCalendar';
import {
  AnalyticsDateFilter,
  DateRange,
  defaultRange,
} from './AnalyticsDateFilter';
import { AnalyticsDailyTrend } from './AnalyticsDailyTrend';
import { AdminBugoFeventListModal } from './AdminBugoFeventListModal';
import { AcqMsgFilter, BugoStatDrillDown, ShareFilter } from './adminBugoStat.types';
import { AnalyticsDayPoint } from './analyticsDay.types';
import {
  aggregateBugoStat,
  brandKeyOf,
  drillDownFevents,
  formatRate,
} from './adminBugoStat.util';
import { exportBugoStatExcel } from './adminBugoStatExcel';
import {
  CenterLoading,
  ChipGroup,
  EmptyBox,
  ErrorBox,
  NoticeBox,
  StatCard,
} from './AnalyticsShared';
import { FEVENT_FETCH_LIMIT, MAX_RANGE_DAYS, useAdminBugoStat } from './useAdminBugoStat';

const ACQ_MSG_OPTIONS = [
  { value: 'all' as AcqMsgFilter, label: '전체' },
  { value: 'used' as AcqMsgFilter, label: '사용' },
  { value: 'unused' as AcqMsgFilter, label: '미사용' },
];

//* 달력·추이 차트에 넘길 축 표기 — 부고 탭은 상가 수 안에 공유 상가를 겹쳐 그린다
const DAY_LABELS = { value: '상가', unit: '건', sub: '공유' } as const;

const SHARE_OPTIONS = [
  { value: 'all' as ShareFilter, label: '전체' },
  { value: 'shared' as ShareFilter, label: '공유' },
  { value: 'unshared' as ShareFilter, label: '미공유' },
];

export const AdminBugoAnalyticsTab: FC = function AdminBugoAnalyticsTab() {
  const [range, setRange] = useState<DateRange>(defaultRange);

  const [acqMsg, setAcqMsg] = useState<AcqMsgFilter>('all');
  const [share, setShare] = useState<ShareFilter>('all');
  const [brandKeys, setBrandKeys] = useState<string[]>([]);
  const [view, setView] = useState<'table' | 'calendar'>('table');
  //* 자세히 보기 — 열려 있으면 어떤 칸을 눌렀는지와 제목을 함께 들고 있는다
  const [drill, setDrill] = useState<{
    drill: BugoStatDrillDown;
    title: string;
  } | null>(null);

  const {
    status,
    fevents,
    bugoDataUnavailable,
    acqMsgDataUnavailable,
    truncated,
    errorMessage,
    resolveBrandName,
    refetch,
  } = useAdminBugoStat(range.from, range.to);

  //* 상조 칩 목록 — 상조 필터와 무관하게 고정돼야 하므로 원자료에서 뽑는다
  const brandOptions = useMemo(() => {
    const counts = new Map<string, { key: string; name: string; count: number }>();
    fevents.forEach((fevent) => {
      const key = brandKeyOf(fevent);
      const entry = counts.get(key);
      if (entry) entry.count += 1;
      else
        counts.set(key, {
          key,
          name: resolveBrandName(fevent.bugoBrand, fevent.bugoBrandAgencyIndex),
          count: 1,
        });
    });
    return Array.from(counts.values()).sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name),
    );
  }, [fevents, resolveBrandName]);

  //* 기간을 옮겨 선택한 상조의 상가가 없어지면 칩도 사라진다 —
  //* 보이지 않는 필터가 남아 "상가 없음"으로 보이지 않도록 함께 해제한다
  useEffect(() => {
    if (fevents.length === 0) return;
    setBrandKeys((prev) => {
      const available = new Set(brandOptions.map((option) => option.key));
      const next = prev.filter((key) => available.has(key));
      return next.length === prev.length ? prev : next;
    });
  }, [brandOptions, fevents.length]);

  const result = useMemo(
    () =>
      aggregateBugoStat(
        fevents,
        { acqMsg, share, brandKeys },
        range,
        { bugoDataUnavailable, acqMsgDataUnavailable, resolveBrandName },
      ),
    [
      fevents,
      acqMsg,
      share,
      brandKeys,
      range,
      bugoDataUnavailable,
      acqMsgDataUnavailable,
      resolveBrandName,
    ],
  );

  const toggleBrand = (key: string) => {
    setBrandKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  //* 공용 달력·추이 컴포넌트가 쓰는 중립 형태로 옮긴다
  const dayPoints: AnalyticsDayPoint[] = useMemo(
    () =>
      result.days.map((day) => ({
        date: day.date,
        value: day.feventCount,
        subValue: day.sharedFeventCount,
      })),
    [result.days],
  );

  //* 열려 있을 때만 계산 (닫혀 있으면 전체 목록을 헛돌릴 이유가 없다)
  const drillFevents = useMemo(
    () =>
      drill
        ? drillDownFevents(fevents, { acqMsg, share, brandKeys }, drill.drill)
        : [],
    [drill, fevents, acqMsg, share, brandKeys],
  );

  const totals = result.totals;

  return (
    <div className="space-y-3">
      {/* 필터 */}
      <div className="space-y-2.5 rounded-lg bg-white p-3 shadow-smd">
        <AnalyticsDateFilter
          label="발인일"
          range={range}
          onChange={setRange}
          maxRangeDays={MAX_RANGE_DAYS}
        />

        <ChipGroup
          label="조의글"
          value={acqMsg}
          options={ACQ_MSG_OPTIONS}
          onChange={setAcqMsg}
        />
        <ChipGroup label="공유" value={share} options={SHARE_OPTIONS} onChange={setShare} />

        {brandOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="w-11 shrink-0 text-13 font-medium text-gray-600">상조</span>
            <button
              type="button"
              onClick={() => setBrandKeys([])}
              className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                brandKeys.length === 0
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              전체
            </button>
            {brandOptions.map((brand) => (
              <button
                key={brand.key}
                type="button"
                onClick={() => toggleBrand(brand.key)}
                className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                  brandKeys.includes(brand.key)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {brand.name}
                <span className="ml-1 tabular-nums opacity-70">{brand.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {status === 'loading' && <CenterLoading />}
      {status === 'error' && (
        <ErrorBox message={errorMessage ?? '오류가 발생했습니다.'} onRetry={refetch} />
      )}

      {status === 'success' && (
        <>
          {truncated && (
            <NoticeBox tone="warn">
              조회 상한 {FEVENT_FETCH_LIMIT.toLocaleString()}건에 걸려 일부만 집계했습니다.
              기간을 좁혀주세요.
            </NoticeBox>
          )}
          {bugoDataUnavailable && (
            <NoticeBox tone="warn">
              부고(상주) 정보를 불러오지 못해 발송·미발송 수치는 집계에서 빠졌습니다.
            </NoticeBox>
          )}
          {acqMsgDataUnavailable && !bugoDataUnavailable && (
            <NoticeBox tone="warn">
              조의글을 불러오지 못해 조의글 개수는 집계에서 빠졌습니다.
            </NoticeBox>
          )}

          {totals.feventCount === 0 ? (
            <EmptyBox message="해당 조건의 상가가 없습니다." />
          ) : (
            <>
              {/* 요약 */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatCard
                  label="상가 수"
                  value={totals.feventCount.toLocaleString()}
                  sub={`상조 ${result.brands.length}곳`}
                  accent="indigo"
                />
                <StatCard
                  label="공유 상가"
                  value={totals.sharedFeventCount.toLocaleString()}
                  sub={`공유율 ${formatRate(totals.sharedFeventCount, totals.feventCount)}`}
                  accent="emerald"
                />
                <StatCard
                  label="미공유 상가"
                  value={totals.unsharedFeventCount.toLocaleString()}
                  sub={`전체의 ${formatRate(
                    totals.unsharedFeventCount,
                    totals.feventCount,
                  )}`}
                  accent="red"
                />
                <StatCard
                  label="총 공유 횟수"
                  value={totals.shareCountTotal.toLocaleString()}
                  sub={`상가당 평균 ${
                    totals.feventCount > 0
                      ? (totals.shareCountTotal / totals.feventCount).toFixed(1)
                      : '-'
                  }회`}
                />
                <StatCard
                  label="부고(상주) 수"
                  value={totals.bugoCount.toLocaleString()}
                  sub={`상가당 ${
                    totals.feventCount > 0
                      ? (totals.bugoCount / totals.feventCount).toFixed(1)
                      : '-'
                  }명`}
                />
                <StatCard
                  label="발송 완료"
                  value={totals.sentBugoCount.toLocaleString()}
                  sub={`발송률 ${formatRate(totals.sentBugoCount, totals.bugoCount)}`}
                  accent="emerald"
                />
                <StatCard
                  label="미발송"
                  value={totals.unsentBugoCount.toLocaleString()}
                  sub={`한 건도 못 보낸 상가 ${totals.unsentFeventCount.toLocaleString()}곳`}
                  accent="orange"
                />
                <StatCard
                  label="조의글 설정 상가"
                  value={totals.acqMsgFeventCount.toLocaleString()}
                  sub={`설정률 ${formatRate(
                    totals.acqMsgFeventCount,
                    totals.feventCount,
                  )}`}
                />
                <StatCard
                  label="조의글 개수"
                  value={
                    acqMsgDataUnavailable ? '-' : totals.acqMsgTotal.toLocaleString()
                  }
                  //* 평균 분모는 '글 달린 상가' — 안 달린 곳까지 나누면 희석된다
                  sub={
                    acqMsgDataUnavailable
                      ? '집계 불가'
                      : `글 달린 상가당 ${
                          totals.acqMsgWrittenFeventCount > 0
                            ? (
                                totals.acqMsgTotal / totals.acqMsgWrittenFeventCount
                              ).toFixed(1)
                            : '-'
                        }개`
                  }
                  accent="indigo"
                />
                <StatCard
                  label="조의글 달린 상가"
                  value={
                    acqMsgDataUnavailable
                      ? '-'
                      : totals.acqMsgWrittenFeventCount.toLocaleString()
                  }
                  //* 설정만 켜고 글이 하나도 안 달린 상가를 바로 알 수 있게 같이 보여준다
                  sub={
                    acqMsgDataUnavailable
                      ? '집계 불가'
                      : `설정 대비 ${formatRate(
                          totals.acqMsgWrittenFeventCount,
                          totals.acqMsgFeventCount,
                        )}`
                  }
                />
              </div>

              <NoticeBox>
                공유 수치는 서버가 발인일 전까지의 공유만 집계합니다(발인 후 공유는 세지
                않음). "조의글 설정"은 상가별 기능을 켠 것이고, "조의글 개수"는 실제로 달린
                글 수입니다 — 차단글과 상조 고정 문구("OO 임직원 일동")는 빠집니다. 평균은
                글이 달린 상가만 분모로 씁니다.
              </NoticeBox>

              {/* 표 / 달력 전환 */}
              <div className="flex items-center gap-1.5">
                {(
                  [
                    { key: 'table' as const, label: '표' },
                    { key: 'calendar' as const, label: '달력' },
                  ]
                ).map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setView(item.key)}
                    className={`rounded-lg px-4 py-1.5 text-14 font-medium ${
                      view === item.key
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setDrill({ drill: {}, title: '전체 상가' })
                  }
                  className="ml-auto rounded-lg bg-white px-3 py-1.5 text-14 font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  전체 목록 보기
                </button>
              </div>

              {view === 'table' ? (
                <>
                  <AnalyticsDailyTrend
                    title="발인일별 상가 수"
                    days={dayPoints}
                    labels={DAY_LABELS}
                  />
                  <AdminBugoBrandTable
                    result={result}
                    onExport={() => exportBugoStatExcel(result, range)}
                    onBrandClick={(brandKey, brandName) =>
                      setDrill({ drill: { brandKey }, title: brandName })
                    }
                  />
                </>
              ) : (
                <AnalyticsCalendar
                  days={dayPoints}
                  labels={DAY_LABELS}
                  hint="날짜나 주간 칸을 누르면 그 기간에 발인한 상가 목록이 열립니다."
                  onDayClick={(date) =>
                    setDrill({ drill: { date }, title: `${date} 발인` })
                  }
                  onWeekClick={(from, to, label) =>
                    setDrill({ drill: { dateFrom: from, dateTo: to }, title: label })
                  }
                />
              )}
            </>
          )}
        </>
      )}

      {drill && (
        <AdminBugoFeventListModal
          title={drill.title}
          subtitle={`${range.from} ~ ${range.to}`}
          fevents={drillFevents}
          bugoDataUnavailable={bugoDataUnavailable}
          acqMsgDataUnavailable={acqMsgDataUnavailable}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};
